<template>
  <ion-card v-if="fileMessage" class="attach-card pa-sm" color="white"
    ><img v-if="fileMessage.fileType !== 'pdf'" :src="fileMessage.fileUrl" />
    <div v-else><img :src="pdfTemplate" class="w-100 h-100 pdf-img" /></div>
  </ion-card>
  <div class="d-flex w-100 gap-sm align-items-center">
    <ion-textarea
      class="bg-white chat"
      :placeholder="t('support.chat.writeMessage')"
      fill="outline"
      shape="round"
      :auto-grow="true"
      maxLength="500"
      v-model.trim="message"
      @keydown.enter="onSendMessage"
    >
      <ion-button
        :disabled="isLoading"
        class="btn-send"
        fill="clear"
        slot="end"
        id="click-trigger"
        shape="round"
      >
        <ion-icon slot="icon-only" :icon="happyOutline"></ion-icon>
      </ion-button>

      <UploadFileChat :isDisabled="isLoading" @file-selected="onFileSelected" />
    </ion-textarea>

    <ion-popover trigger="click-trigger" reference="trigger" trigger-action="click">
      <ion-content class="ion-padding w-100"
        ><EmojiPicker
          :native="true"
          @select="onSelectEmoji"
          :static-texts="{
            placeholder: t('support.chat.searchEmoji'),
            skinTone: t('support.chat.skinTone'),
          }"
      /></ion-content>
    </ion-popover>

    <div class="send align-items-center">
      <ion-button :disabled="isLoading" shape="round" class="send" @click="onSendMessage">
        <ion-icon slot="icon-only" :icon="send"></ion-icon>
      </ion-button>
    </div>
  </div>
  <ion-progress-bar v-if="isLoading" type="indeterminate" class="mt-xs"></ion-progress-bar>
</template>

<script setup lang="ts">
  import { useAuth } from '@/composables/useAuth';
  import { useChatStore } from '@/stores/chat-store';
  import {
    IonButton,
    IonIcon,
    IonTextarea,
    IonPopover,
    IonContent,
    IonCard,
    IonProgressBar,
  } from '@ionic/vue';
  import { happyOutline, send } from 'ionicons/icons';
  import { onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import EmojiPicker from 'vue3-emoji-picker';
  import 'vue3-emoji-picker/css';
  import UploadFileChat from './UploadFileChat.vue';
  import { FileMessage } from '@/interfaces/chat/chat.interface';
  import { User } from '@/interfaces/auth/auth-store.interface';
  import pdfTemplate from '@/assets/images/pdf-template.png';
  import { useChat } from '@/composables/useChat';

  const props = withDefaults(
    defineProps<{
      bookingId?: string;
      socket: any;
      user: User;
    }>(),
    {},
  );

  const { t } = useI18n();
  const message = ref('');
  const { fullName } = useAuth();
  const fileMessage = ref<FileMessage>();
  const isLoading = ref(false);

  const { setNewMessage } = useChatStore();
  const { handleUploadChatImage } = useChat();

  interface Emoji {
    i: string;
  }

  const onSelectEmoji = (emoji: Emoji) => {
    message.value += emoji.i;
  };

  const onFileSelected = async (file: any) => {
    isLoading.value = true;
    const response = await handleUploadChatImage(file, props.bookingId as string);
    if (response) {
      fileMessage.value = response;
    }

    isLoading.value = false;
  };

  const onSendMessage = () => {
    if (message.value.trim().length > 0 || fileMessage.value) {
      const newMessage = {
        reservationId: props.bookingId as string,
        message: message.value,
        senderName: fullName.value,
        senderRole: 'tenant',
        file: fileMessage.value,
        userToNotifyId: props.user.id,
      };

      props.socket.emit('f_send_message', newMessage);

      setNewMessage({
        ...newMessage,
        sentAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        fileUrl: fileMessage.value ? fileMessage.value.fileUrl : null,
        fileType: fileMessage.value ? fileMessage.value.fileType : undefined,
      });
      message.value = ' ';
      fileMessage.value = undefined;
    } else {
      message.value = ' ';
    }
  };

  const setPadding = () => {
    const textareas = document.getElementsByClassName('native-textarea');
    if (textareas && textareas.length > 0) {
      for (const element of textareas as any) {
        element.style.paddingTop = '20px';
      }
    } else {
      setTimeout(() => {
        setPadding();
      }, 50);
    }
  };

  onMounted(() => {
    setPadding();
  });
</script>

<style lang="scss" scoped>
  .chat {
    border-radius: 35px;
  }
  .send {
    width: 49px;
    height: 49px;
  }

  ion-popover {
    --backdrop-opacity: 0.6;
    --box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.6);
    --color: white;
    --width: 300px;
  }

  .v3-emoji-picker {
    margin: 0px 0px 0px -5px !important;
  }

  ion-textarea {
    --padding-top: 0px !important;
    --padding-bottom: 0px !important;
  }

  textarea {
    padding-top: 20px !important;
  }

  .btn-send {
    margin-top: 13px;
  }

  .attach-card {
    position: relative;
    z-index: 99;
    width: fit-content;

    img {
      max-height: 65px;
      border-radius: 4px;
      width: 100px;
      object-fit: cover;
    }
  }
</style>
